import React, {useState, useEffect, ReactElement} from 'react';
import {useLocation} from 'react-router-dom';
import {useDispatch} from 'react-redux';
import Row from '@frontend/ui-kit/Components/Row';
import Column from '@frontend/ui-kit/Components/Column';
import Heading, {HEADING_TYPES} from '@frontend/ui-kit/Components/Heading';
import Text from '@frontend/ui-kit/Components/Text';
import Input from '@frontend/ui-kit/Components/Input';
import Button from '@frontend/ui-kit/Components/Button';
import Link from '@frontend/ui-kit/Components/Link';
import {ICON_TYPES} from '@frontend/ui-kit/Components/Icon';
import SFTPPasswordPlaceholder from '../../../static/images/SFTPPasswordPlaceholder.svg';
import {requestSFTPSecretValidation, requestSFTPPasswordGeneration} from '../../../actions/sftp';
import analyticsService, {ANALYTICS_EVENTS} from '../../../analyticsService';
import {AppDispatch} from '../../../store';
import {parseQuery} from '../../../utils';
import {CSS_TEAM_EMAIL} from '../../../constants';
import './index.scss';

const PasswordGenerationContent = (): ReactElement => {
    const dispatch = useDispatch<AppDispatch>();
    const {search, pathname} = useLocation();
    const {secret} = parseQuery(search);
    const [isLinkValid, setIsLinkValid] = useState<boolean>(false);
    const [password, setPassword] = useState<string>('');
    const [tooltipContent, setTooltipContent] = useState<string>('Copy');
    // FYI: Segment adds the page URL to every event, so we override it to keep the one-time `secret` query param out of analytics (Slavik, 10.21.2026)
    const analyticsOptions = {context: {page: {url: `${window.location.origin}${pathname}`, search: ''}}};
    const passwordInputProps = {
        value: password,
        disabled: true,
        placeholder: ' ',
        tooltipProps: {content: tooltipContent, hideOnClick: false, onHidden: () => setTooltipContent('Copy')},
        icon: ICON_TYPES.copy,
        onIconClick: () => {
            navigator.clipboard.writeText(password);
            setTooltipContent('Copied to Clipboard');
        }
    };

    const onGeneratePassword = async () => {
        const {password, isSuccess, statusCode} = await dispatch(requestSFTPPasswordGeneration(String(secret)));

        analyticsService.track(ANALYTICS_EVENTS.sftp.generatePasswordClicked, {...(!isSuccess && {status_code: statusCode})}, analyticsOptions);

        if (!isSuccess) {
            setIsLinkValid(false);
            return;
        }
        setPassword(password ?? '');
    };

    useEffect(() => {
        (async () => {
            const {validationInfo, isSuccess, statusCode} = await dispatch(requestSFTPSecretValidation(String(secret)));

            analyticsService.track(
                ANALYTICS_EVENTS.sftp.passwordGenerationOpened,
                {is_valid: validationInfo?.is_valid, ...(!isSuccess && {status_code: statusCode})},
                analyticsOptions
            );
            setIsLinkValid(!!validationInfo?.is_valid);
        })();
    }, [secret]);

    return (
        <div className='password-generation-content'>
            <Heading className='mb-24' type={HEADING_TYPES['1']}>Hello!</Heading>

            {isLinkValid && (
                <div data-testid='valid-link-content'>
                    <Text className='mb-16'>
                        Click the button below to generate your password. We do not store your password and it cannot be generated twice. Be sure to secure and keep your password safe. This link will expire in 48 hours.
                        We recommend using a password manager to keep your password secure and safe as the password cannot be recovered if lost or forgotten.
                    </Text>
                    <Text className='mb-27'>
                        Please get in touch with us at <Link href={`mailto:${CSS_TEAM_EMAIL}`}>{CSS_TEAM_EMAIL}</Link> or reach out to your designated Customer Success contact immediately so we can guide you through the next steps.
                    </Text>

                    <Row className='password-generation-section' rowGap='sm' columnGap='xs' middle='xs'>
                        <Column md={8}>
                            <Input {...passwordInputProps}/>
                        </Column>

                        <Column md={4}>
                            <Button className='password-generation-section__button' disabled={!!password} onClick={onGeneratePassword} data-testid='generate-password-button'>
                                Generate Password
                            </Button>
                        </Column>
                    </Row>
                </div>
            )}

            {!isLinkValid && (
                <div data-testid='invalid-link-content'>
                    <Text className='mb-16'>Oh, no. There seems to be a problem. We can't seem to find the page you're looking for.</Text>
                    <Text className='mb-27'>Please contact your Implementation Team for support.</Text>

                    <div className='password-generation-section password-generation-section_placeholder'>
                        <img className='placeholder-image mb-12' src={SFTPPasswordPlaceholder} alt='SFTP Placeholder'/>

                        <Text>Looks like this isn’t valid</Text>
                    </div>
                </div>
            )}
        </div>
    );
};

export {PasswordGenerationContent as TestablePasswordGenerationContent};
export default PasswordGenerationContent;
